import { useState } from "react";
import { CalendarDays, Users, ArrowRight } from "lucide-react";
import DateRangePicker from "./DateRangePicker";

const GUESTY_BASE = "https://aquastays.guestybookings.com/en/properties/69dff123051f000012a8ddb6";

function buildGuestyUrl(checkIn, checkOut, guests) {
  const params = new URLSearchParams();
  if (checkIn)  params.set("checkIn",  checkIn);
  if (checkOut) params.set("checkOut", checkOut);
  if (guests) {
    params.set("guests", guests);
    params.set("minOccupancy", guests);
  }
  const qs = params.toString();
  return qs ? `${GUESTY_BASE}?${qs}` : GUESTY_BASE;
}

function nightsBetween(a, b) {
  if (!a || !b) return 0;
  const diff = new Date(b) - new Date(a);
  return Math.max(0, Math.round(diff / 86400000));
}

// ─── Bar variant (hero / home page, horizontal) ──────────────────────────────
function BookingBar() {
  const today    = new Date().toISOString().split("T")[0];
  const [checkIn,  setCheckIn]  = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests,   setGuests]   = useState("2");

  const nights = nightsBetween(checkIn, checkOut);
  const url    = buildGuestyUrl(checkIn, checkOut, guests);
  const hasRange = nights > 0;

  return (
    <div className="flex flex-col sm:flex-row gap-0 border border-[#1C1C1A]/12 bg-white">
      {/* Check-in */}
      <label className="flex-1 flex flex-col justify-center px-5 py-3.5 border-b sm:border-b-0 sm:border-r border-[#1C1C1A]/10 cursor-pointer group">
        <span className="text-[0.58rem] uppercase tracking-[0.18em] text-[#6B6B65] mb-1 group-hover:text-[#C8A96E] transition-colors">Check in</span>
        <input
          type="date"
          value={checkIn}
          min={today}
          onChange={e => {
            setCheckIn(e.target.value);
            if (checkOut && e.target.value >= checkOut) setCheckOut("");
          }}
          className="text-sm text-[#1C1C1A] font-light bg-transparent focus:outline-none cursor-pointer w-full"
        />
      </label>

      {/* Check-out */}
      <label className="flex-1 flex flex-col justify-center px-5 py-3.5 border-b sm:border-b-0 sm:border-r border-[#1C1C1A]/10 cursor-pointer group">
        <span className="text-[0.58rem] uppercase tracking-[0.18em] text-[#6B6B65] mb-1 group-hover:text-[#C8A96E] transition-colors">Check out</span>
        <input
          type="date"
          value={checkOut}
          min={checkIn || today}
          onChange={e => setCheckOut(e.target.value)}
          className="text-sm text-[#1C1C1A] font-light bg-transparent focus:outline-none cursor-pointer w-full"
        />
      </label>

      {/* Guests */}
      <label className="flex flex-col justify-center px-5 py-3.5 border-b sm:border-b-0 sm:border-r border-[#1C1C1A]/10 cursor-pointer group min-w-[120px]">
        <span className="text-[0.58rem] uppercase tracking-[0.18em] text-[#6B6B65] mb-1 group-hover:text-[#C8A96E] transition-colors">Guests</span>
        <select
          value={guests}
          onChange={e => setGuests(e.target.value)}
          className="text-sm text-[#1C1C1A] font-light bg-transparent focus:outline-none cursor-pointer appearance-none"
        >
          <option value="1">1 guest</option>
          <option value="2">2 guests</option>
        </select>
      </label>

      {/* CTA */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="booking-bar-cta"
        className="btn-accent flex items-center justify-center gap-2 px-8 whitespace-nowrap"
      >
        {hasRange
          ? `${nights} night${nights > 1 ? "s" : ""} — check availability`
          : "Check availability"}
        <ArrowRight size={13} />
      </a>
    </div>
  );
}

// ─── Card variant (boat detail page sidebar, vertical) ───────────────────────
function BookingCard() {
  const [checkIn,  setCheckIn]  = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests,   setGuests]   = useState("2");

  const nights = nightsBetween(checkIn, checkOut);
  const url    = buildGuestyUrl(checkIn, checkOut, guests);

  return (
    <div className="space-y-4">
      {/* Date range picker */}
      <div className="border border-[#1C1C1A]/12 bg-white rounded-lg overflow-visible">
        <DateRangePicker
          checkIn={checkIn}
          checkOut={checkOut}
          onChange={({ checkIn: ci, checkOut: co }) => {
            setCheckIn(ci);
            setCheckOut(co);
          }}
          variant="card"
        />
      </div>

      {/* Guests */}
      <label className="flex flex-col gap-1.5 cursor-pointer">
        <span className="flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.18em] text-[#6B6B65]">
          <Users size={11} strokeWidth={1.5} className="text-[#E8970A]" />
          Guests
        </span>
        <select
          value={guests}
          onChange={e => setGuests(e.target.value)}
          className="w-full border border-[#1C1C1A]/12 bg-white px-3 py-2.5 text-sm text-[#1C1C1A] font-light focus:outline-none focus:border-[#E8970A]/60 transition-colors appearance-none cursor-pointer"
        >
          <option value="1">1 guest</option>
          <option value="2">2 guests</option>
        </select>
      </label>

      {/* Night count (no price — managed in Guesty) */}
      {nights > 0 && (
        <div className="bg-white border border-[#1C1C1A]/8 px-4 py-3 flex items-center justify-between">
          <span className="text-xs text-[#6B6B65] font-light">
            {nights} night{nights > 1 ? "s" : ""} selected
          </span>
          <span className="text-[0.6rem] uppercase tracking-[0.14em] text-[#6B6B65]">
            Pricing shown on next step
          </span>
        </div>
      )}

      {/* CTA */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="boat-book-cta"
        className="flex items-center justify-center gap-2 w-full mt-2 px-7 py-3 rounded-full bg-[#E8970A] hover:bg-[#D08500] text-white font-semibold text-sm transition-colors"
      >
        {nights > 0 ? "Check availability" : "Check availability"}
      </a>

      <p className="text-[0.6rem] text-[#6B6B65] uppercase tracking-[0.14em] text-center">
        Secure booking · Instant confirmation
      </p>
    </div>
  );
}

// ─── Exports ─────────────────────────────────────────────────────────────────
export function BookingWidgetBar()  { return <BookingBar />;  }
export function BookingWidgetCard() { return <BookingCard />; }
